import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function About() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Perry Dasilva",
    "jobTitle": "Social Media Strategist",
    "worksFor": {
      "@type": "Organization",
      "name": "Otso Co"
    },
    "url": "https://otsoco.com/about"
  };

  const perryImage = "https://media-manager-c.questera.ai/greta-media/4320f8aaa9ed428bff142ca67321b3bfce2c39771503d4270b0b625b52f5e6960d1f85712829807a2ee1ac58fcf9446b/images/aW1hZ2UvcG5n/c724ae193239e8cd8dadf828fd04befa.png";

  return (
    <>
      <SEO 
        title="About Perry Dasilva | Social Media Strategist for Pet Care Businesses"
        description="Perry Dasilva is a social media strategist and founder of Otso Co, specializing in helping pet care businesses grow online. Based in Laurel, MD, serving dog walkers, groomers, trainers, and pet businesses nationwide."
        path="/about"
        schema={schema}
      />

      {/* Header */}
      <section className="bg-brand-primaryDark text-brand-white py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            Hey, I am Perry. <span className="text-brand-blush">Let's Keep It Real.</span>
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 px-4 bg-brand-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-16 items-center">
          <div className="w-full md:w-1/2">
            <img src={perryImage} alt="Perry Dasilva social media strategist for pet businesses founder of Otso Co Laurel Maryland" className="rounded-2xl shadow-xl border-4 border-brand-blush w-full max-w-md mx-auto h-auto object-cover aspect-square" />
          </div>
          <div className="w-full md:w-1/2 text-lg text-gray-700 leading-relaxed space-y-6">
            <p>
              I am a social media strategist based in Laurel, Maryland and I work exclusively with pet care businesses. Dog walkers, groomers, trainers, boarders, sitters, pet daycares — that is my world. 
            </p>
            <p>
              I started Otso Co because I kept seeing the same problem. Pet business owners are incredible at what they do. They love animals, they are great with clients, and they work incredibly hard. But social media? That is a whole different skill set, and most people are just winging it.
            </p>
            <p className="font-bold text-brand-primaryDark text-xl">
              I am here to fix that.
            </p>
          </div>
        </div>
      </section>

      {/* Background Story */}
      <section className="py-24 px-4 bg-brand-blush/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center text-brand-primaryDark">My Background</h2>
          <div className="bg-brand-white p-8 md:p-12 rounded-3xl shadow-lg text-lg text-gray-700 leading-relaxed space-y-6 border-t-8 border-brand-mediumPurple">
            <p>
              Before social media marketing, I spent nearly a decade in operations management overseeing 6 million dollars in annual revenue across 20 plus commercial properties. That background taught me how to build systems, stay organized under pressure, and focus on results, not just activity.
            </p>
            <p>
              When I moved into marketing, I brought all of that with me. I do not just create pretty content. I build strategies that work like systems so your social media is consistent, intentional, and always working toward one goal: more clients for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Who I Work With */}
      <section className="py-24 px-4 bg-brand-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center text-brand-primaryDark">Who I Work With</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "Dog Walkers and Pet Sitters",
              "Groomers and Stylists",
              "Trainers and Behaviorists",
              "Pet Daycares and Boarding Facilities"
            ].map((title, i) => (
              <div key={i} className="bg-brand-primaryDark text-brand-white p-8 rounded-2xl shadow-xl border-b-4 border-brand-blush text-center flex items-center justify-center min-h-[160px]">
                <h3 className="text-xl font-bold leading-snug">{title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fun Facts */}
      <section className="py-24 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center text-brand-primaryDark">A Few More Things</h2>
          <ul className="space-y-6 text-xl font-medium bg-brand-white p-10 rounded-3xl shadow-sm border border-gray-100">
            {[
              "Based in Laurel, Maryland",
              "Proud dog mom to a Shiba Inu named Choco",
              "Currently on a mission to teach Choco new tricks (he has opinions about this)"
            ].map((fact, i) => (
              <li key={i} className="flex items-start">
                <div className="w-3 h-3 rounded-full bg-brand-mediumPurple mt-2.5 mr-4 flex-shrink-0"></div>
                <span className="text-gray-800">{fact}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-brand-primaryDark text-brand-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold mb-6 text-brand-blush">Want to Work Together?</h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            If you are a pet care business owner who is tired of guessing on social media, let's talk. Book a free discovery call and let's figure out exactly what your business needs.
          </p>
          <a 
            href="https://tidycal.com/digital-by-perry/schedule-a-call" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block bg-brand-mediumPurple hover:bg-brand-mauve text-brand-white px-10 py-5 rounded-lg font-bold text-xl transition-colors shadow-lg"
          >
            Book a Free Discovery Call
          </a>
        </div>
      </section>
    </>
  );
}